import { Box, Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { omit } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { ExportButton } from '@/components/ExportButton/ExportButton';
import QuickSearch from '@/components/QuickSearch';
import TableWidget from '@/components/TableWidget';
import { EXCLUDED_INTERNAL_ORGS, PAGE_SIZES } from '@/constants/common';
import { YEAR_MONTH_DAY_TIME_FORMAT } from '@/constants/date.constants';
import {
  refetchInternalTicketsAtom,
  ticketSearchParamsAtom
} from '@/pages/Dashboard/Dashboard.store';
import ticketsTableColumns, {
  TEAM_QUEUE_COLUMNS
} from '@/pages/Dashboard/DashboardInternal/DashboardInternal.constants';
import type { TicketWithoutUserOwner } from '@/pages/Dashboard/DashboardInternal/DashboardInternal.types';
import {
  getDueNowTickets,
  getNextActionOwnerTickets,
  getPastDueTickets,
  getRiskyTickets
} from '@/pages/Dashboard/DashboardInternal/DashboardInternal.utils';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import type { Ticket } from '@/pages/Tickets';
import { TICKET_STATUS } from '@/pages/Tickets/Tickets.constants';
import { useFetchV2Tickets } from '@/pages/Tickets/Tickets.store';

import { TEAM_QUEUE } from '../TicketCreateGeneralInformation/TicketCreateGeneralInformation.constants';
import useStyles from './InternalTicketsTables.styles';

const InternalTicketsTables = () => {
  const ticketSearchParams = useAtomValue(ticketSearchParamsAtom);
  const selectedOrg = useAtomValue(selectedOrganizationAtom);
  const refetchInternalTickets = useAtomValue(refetchInternalTicketsAtom);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [search, setSearch] = useState<string>('');
  const [showTable, setShowTable] = useState(false);
  const [cellFilter, setCellFilter] = useState<{
    field: string;
    teamQueue: string;
  }>();
  const { pathname } = useLocation();

  const { classes } = useStyles({
    onDashboard: pathname.includes('dashboard')
  });

  const tenYearsAgo = useMemo(() => {
    return dayjs().subtract(10, 'year').format(YEAR_MONTH_DAY_TIME_FORMAT);
  }, []);

  const internalBaseSearchParams = useMemo(() => {
    return omit(ticketSearchParams, 'published_at__gte');
  }, [ticketSearchParams]);

  const internalSearchParams = useMemo(() => {
    return {
      ...internalBaseSearchParams,
      published_at__gte: tenYearsAgo,
      ticket_status__name__in: [
        TICKET_STATUS.OPEN,
        TICKET_STATUS.ACKNOWLEDGED,
        TICKET_STATUS.REMEDIATED
      ]
    };
  }, [internalBaseSearchParams, tenYearsAgo]);

  const { data: unfilteredOpenTickets, isFetching } = useFetchV2Tickets({
    enabled: true,
    searchParams: internalSearchParams,
    fetchAllPages: true,
    refetchIntervalMs: refetchInternalTickets ? 60000 : undefined
  });

  const getNonTestTickets = () => {
    return unfilteredOpenTickets?.filter(
      (ticket: Ticket) =>
        !EXCLUDED_INTERNAL_ORGS.includes(ticket.organization?.label || '')
    );
  };
  const orgCorrectedTickets = EXCLUDED_INTERNAL_ORGS.includes(selectedOrg.label)
    ? unfilteredOpenTickets
    : getNonTestTickets();

  const openTickets = orgCorrectedTickets;

  // Filter out the 'user' and 'owner' properties from each ticket object
  const noUserOwnerTickets: TicketWithoutUserOwner[] =
    openTickets?.map((ticket: Ticket) => {
      const updatedTicket = omit(ticket, ['user', 'owner']);
      return updatedTicket as TicketWithoutUserOwner; // Return the new object without 'user' or 'owner'
    }) || [];

  const unassignedTickets = useMemo(
    () => noUserOwnerTickets.filter((ticket) => ticket.team_queue === ''),
    [noUserOwnerTickets]
  );

  const sortedTickets = useMemo(() => {
    const sorted = [
      {
        teamQueue: 'All',
        open: noUserOwnerTickets.length,
        dueNow: getDueNowTickets(noUserOwnerTickets).length,
        pastDue: getPastDueTickets(noUserOwnerTickets).length,
        nextActionOwner: getNextActionOwnerTickets(noUserOwnerTickets).length,
        riskyTickets: getRiskyTickets(noUserOwnerTickets).length,
        id: 0,
        tickets: noUserOwnerTickets
      },
      {
        teamQueue: 'Unassigned',
        open: unassignedTickets.length,
        dueNow: getDueNowTickets(unassignedTickets).length,
        pastDue: getPastDueTickets(unassignedTickets).length,
        nextActionOwner: getNextActionOwnerTickets(unassignedTickets).length,
        riskyTickets: getRiskyTickets(unassignedTickets).length,
        id: 1,
        tickets: unassignedTickets
      }
    ];

    // Only show open, acknowledged, or remediated tickets on this page, all others are filtered out
    TEAM_QUEUE.forEach((team, index) => {
      const teamTickets = noUserOwnerTickets.filter(
        (ticket) => ticket.team_queue === team
      );

      const dueNow = getDueNowTickets(teamTickets);
      const pastDue = getPastDueTickets(teamTickets);
      const riskyTickets = getRiskyTickets(teamTickets);
      const nextActionOwner = getNextActionOwnerTickets(teamTickets);

      sorted.push({
        teamQueue: team,
        open: teamTickets.length,
        dueNow: dueNow.length,
        pastDue: pastDue.length,
        nextActionOwner: nextActionOwner.length,
        riskyTickets: riskyTickets.length,
        id: index + 2,
        tickets: teamTickets
      });
    });

    return sorted;
  }, [noUserOwnerTickets, unassignedTickets]);

  const filteredTickets = useMemo(() => {
    if (!search && !cellFilter) {
      return noUserOwnerTickets;
    }

    if (search && !cellFilter) {
      return noUserOwnerTickets.filter((u) =>
        JSON.stringify(u).toLowerCase().includes(search.toLowerCase())
      );
    }

    if (cellFilter) {
      // Get fresh tickets based on team queue
      let teamTickets: typeof noUserOwnerTickets;

      if (cellFilter.teamQueue === 'All') {
        teamTickets = noUserOwnerTickets;
      } else if (cellFilter.teamQueue === 'Unassigned') {
        teamTickets = unassignedTickets;
      } else {
        teamTickets = noUserOwnerTickets.filter(
          (ticket) => ticket.team_queue === cellFilter.teamQueue
        );
      }

      // Apply the field filter
      let filteredTeamTickets: typeof teamTickets;
      switch (cellFilter.field) {
        case 'nextActionOwner':
          filteredTeamTickets = getNextActionOwnerTickets(teamTickets);
          break;
        case 'dueNow':
          filteredTeamTickets = getDueNowTickets(teamTickets);
          break;
        case 'pastDue':
          filteredTeamTickets = getPastDueTickets(teamTickets);
          break;
        case 'riskyTickets':
          filteredTeamTickets = getRiskyTickets(teamTickets);
          break;
        default:
          filteredTeamTickets = teamTickets;
          break;
      }

      return filteredTeamTickets.filter((u) =>
        JSON.stringify(u).toLowerCase().includes(search.toLowerCase())
      );
    }

    return [];
  }, [search, cellFilter, noUserOwnerTickets, unassignedTickets]);

  const handleCellClick = (params: GridCellParams) => {
    setSearch('');
    setShowTable(true);
    setCellFilter({
      field: params.field,
      teamQueue: params.row.teamQueue
    });
    setSelectedTeam(`${params.row.teamQueue} - ${params.colDef.headerName}`);
  };

  const customHeader = useCallback(() => {
    return (
      <div>
        <div className={classes.ticketsTableHeaderContainer}>
          <Box className={classes.headerLeft}>
            <Typography variant="h4">
              {selectedTeam || 'All - Team Queue'} Tickets
            </Typography>
          </Box>
          <Box className={classes.headerRight}>
            <Box className={classes.quickSearch}>
              <QuickSearch onChange={(term) => setSearch(term)} border />
            </Box>
            {/* TODO: Create friendly column labels for export */}
            <ExportButton
              data={filteredTickets}
              fileName={`${selectedTeam || 'All - Team Queue'} Tickets`}
              json
              csv
            />
          </Box>
        </div>
      </div>
    );
  }, [selectedTeam, classes]);

  return (
    <>
      <div className={classes.teamQueueTicketsTable}>
        <TableWidget
          title="Team Queue Tickets"
          columns={TEAM_QUEUE_COLUMNS}
          rows={sortedTickets}
          loading={isFetching}
          header
          rowHeight={pathname.includes('dashboard') ? 50 : null}
          onCellClick={handleCellClick}
          density={pathname.includes('dashboard') ? 'standard' : 'compact'}
        />
      </div>
      {!showTable && (
        <Typography className={classes.text} variant="body1">
          This shows all tickets from the past 10 years. This includes only
          tickets with a status of open, acknowledged, or remediated. Clicking
          on the team queue cell or open cell will show all tickets for that
          team queue. Clicking on the other cells will show tickets with the
          respective filter applied. Additional filtering is available in the
          header bar options menu.{' '}
        </Typography>
      )}
      {showTable && (
        <div className={classes.ticketsTableContainer}>
          <div className={classes.ticketsTable}>
            <TableWidget
              pageSizeOptions={PAGE_SIZES}
              title="Tickets"
              columns={ticketsTableColumns}
              rows={filteredTickets}
              loading={isFetching}
              header
              onRowClick={(params) =>
                window.open(`/ticket/${params.row.id}`, '_blank')
              }
              customHeader={customHeader}
              density={pathname.includes('dashboard') ? 'standard' : 'compact'}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default InternalTicketsTables;
